import { useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  Landmark, Users, LineChart, Coins, TrendingUp, LayoutGrid, ShieldCheck, BarChart3,
  ArrowUpRight, ArrowDownRight, ChevronRight, Info,
} from "lucide-react";
import { Navbar } from "@/components/site/Navbar";
import { Footer } from "@/components/site/Footer";
import { VideoModal } from "@/components/site/VideoModal";
import { BistChart } from "@/components/site/BistChart";
import { Reveal } from "@/components/site/Reveal";
import { ASSETS } from "@/lib/content";

const PRICES = ["9.210", "8.060", "7.840", "6.730", "5.340"];

const STATS = [
  { icon: Landmark, big: "500+", label: "Halka Açık Şirket" },
  { icon: Users, big: "8 Milyon+", label: "Yatırımcı" },
  { icon: LineChart, big: "1,5 Trilyon ₺+", label: "Piyasa Değeri" },
  { icon: Coins, big: "10 Milyar ₺+", label: "Günlük İşlem Hacmi" },
];

const ADVANTAGES = [
  { icon: TrendingUp, h: "Yüksek Getiri Potansiyeli", p: "Genç ve dinamik şirketlerin büyüme potansiyeli yüksek getiri fırsatları sunar." },
  { icon: LayoutGrid, h: "Çeşitlendirme İmkanı", p: "Farklı sektörlerdeki yüzlerce şirket ile portföyünüzü çeşitlendirebilirsiniz." },
  { icon: ShieldCheck, h: "Likit ve Şeffaf Piyasa", p: "Gelişmiş altyapısı ve şeffaf yapısıyla güvenli bir yatırım ortamı sunar." },
  { icon: BarChart3, h: "Uzun Vadeli Büyüme", p: "Türkiye ekonomisinin büyümesine paralel olarak şirket değerleri de artış gösterir." },
];

const INDICATORS = [
  { label: "BIST 100 Endeksi", value: "10.245", change: "1,35%", up: true },
  { label: "Piyasa Değeri", value: "1,53 Trilyon ₺", change: "0,89%", up: true },
  { label: "Günlük İşlem Hacmi", value: "12,8 Milyar ₺", change: "-0,45%", up: false },
  { label: "Yabancı Oranı", value: "36,2%", change: "0,15%", up: true },
  { label: "Halka Açıklık Oranı", value: "28%", change: "0,20%", up: true },
];

export default function Landing() {
  const [videoOpen, setVideoOpen] = useState(false);
  const { scrollY } = useScroll();
  const heroScale = useTransform(scrollY, [0, 600], [1, 1.08]);

  return (
    <div className="bg-white overflow-x-hidden">
      <Navbar />
      <VideoModal open={videoOpen} onClose={() => setVideoOpen(false)} />

      {/* ================= HERO ================= */}
      <section id="anasayfa" className="relative min-h-[640px] lg:min-h-[720px] flex items-center overflow-hidden bg-navy-950">
        <motion.div style={{ scale: heroScale }} className="absolute inset-0">
          <img src={ASSETS.heroBg} alt="Türkiye Borsası" className="h-full w-full object-cover" />
        </motion.div>
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/80 to-navy-950/10" />
        <div className="absolute inset-y-0 right-0 w-64 bg-gradient-to-l from-navy-950/70 to-transparent hidden md:block" />

        {/* price levels */}
        <div className="hidden md:flex flex-col gap-5 lg:gap-7 absolute right-6 lg:right-12 top-1/2 -translate-y-1/2 z-10">
          {PRICES.map((p, i) => (
            <motion.div
              key={p}
              initial={{ opacity: 0, x: 24 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.9 + i * 0.1, duration: 0.6 }}
              className="flex items-center gap-3 justify-end"
            >
              <span className="h-px w-6 bg-white/50" />
              <span className="font-display text-white/85 text-xl lg:text-2xl font-semibold tabular-nums">{p}</span>
            </motion.div>
          ))}
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8 w-full pt-24 pb-12">
          <div className="max-w-2xl">
            <span className="reveal-line">
              <motion.h1
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ delay: 0.15, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="font-display font-black text-white text-4xl sm:text-5xl lg:text-6xl leading-none tracking-tight whitespace-nowrap"
              >
                TÜRKİYE BORSASI
              </motion.h1>
            </span>
            <span className="reveal-line mt-2">
              <motion.h2
                initial={{ y: "110%" }}
                animate={{ y: "0%" }}
                transition={{ delay: 0.35, duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
                className="font-display font-bold text-brand-400 text-xl sm:text-3xl lg:text-4xl leading-tight tracking-tight"
              >
                DÜNÜ, BUGÜNÜ VE YARINI
              </motion.h2>
            </span>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.6, duration: 0.8 }}
              className="mt-6 text-white/75 text-base sm:text-lg leading-relaxed max-w-md"
            >
              Güçlü geçmişi, dinamik bugünü ve yükselen potansiyeliyle Türkiye Borsası,
              yatırımcılarına değer yaratmaya devam ediyor.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8, duration: 0.8 }}
              className="mt-8 flex flex-wrap gap-4"
            >
              <button
                data-testid="hero-explore-btn"
                onClick={() => window.__lenis?.scrollTo(document.getElementById("bugun"), { offset: -80 })}
                className="group inline-flex items-center gap-2 rounded-md bg-brand-600 hover:bg-brand-500 text-white px-7 py-3.5 font-semibold transition-all shadow-lg shadow-brand-900/40"
              >
                Borsayı Keşfet
                <ChevronRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <button
                data-testid="hero-video-btn"
                onClick={() => setVideoOpen(true)}
                className="inline-flex items-center gap-2 rounded-md bg-brand-400 hover:bg-brand-300 text-white px-7 py-3.5 font-semibold tracking-wide uppercase transition-all"
              >
                Eğitim Videosu
              </button>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ================= TARİHÇE + BUGÜN ================= */}
      <section className="bg-white py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid gap-14 lg:gap-16 lg:grid-cols-2">
          {/* Tarihçe */}
          <div id="tarihce">
            <Reveal>
              <div className="flex items-center gap-3">
                <span className="h-7 w-1 rounded bg-brand-600" />
                <h2 className="font-display font-black text-navy-900 text-2xl sm:text-3xl tracking-tight">TARİHÇE</h2>
              </div>
              <p className="mt-3 font-display font-semibold text-navy-800 text-lg">Geçmişten gelen sağlam temeller</p>
            </Reveal>
            <div className="mt-7 flex gap-6">
              <Reveal delay={0.1} className="shrink-0">
                <div className="overflow-hidden rounded-lg w-28 sm:w-36 shadow-lg">
                  <img src={ASSETS.historic} alt="Tarihi borsa binası" className="w-full h-full object-cover sepia-[0.35]" />
                </div>
              </Reveal>
              <Reveal delay={0.2}>
                <div className="space-y-4 text-slate-600 text-sm leading-relaxed">
                  <p>Borsa İstanbul'un temelleri 1866 yılında Dersaadet Tahvilat Borsası'nın kurulmasıyla atılmıştır. 1985 yılında modern anlamda yeniden yapılanan borsa, 2013 yılında Borsa İstanbul adıyla faaliyetlerini sürdürmeye başlamıştır.</p>
                  <p>100 yılı aşkın geçmişiyle Türkiye sermaye piyasalarının merkezinde yer alan Borsa İstanbul, bugün uluslararası standartlarda hizmet sunan, bölgesinin önde gelen borsalarından biri haline gelmiştir.</p>
                </div>
              </Reveal>
            </div>
          </div>

          {/* Bugün */}
          <div id="bugun">
            <Reveal>
              <div className="flex items-center gap-3">
                <span className="h-7 w-1 rounded bg-brand-600" />
                <h2 className="font-display font-black text-navy-900 text-2xl sm:text-3xl tracking-tight">BUGÜN</h2>
              </div>
              <p className="mt-3 font-display font-semibold text-navy-800 text-lg">Türkiye ekonomisinin aynası</p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-5 text-slate-600 text-sm leading-relaxed">
                Borsa İstanbul, 500'ün üzerinde halka açık şirketi, milyonlarca yatırımcısı ve güçlü işlem hacmiyle Türkiye ekonomisinin barometresi konumundadır.
              </p>
            </Reveal>
            <div className="mt-9 grid grid-cols-2 sm:grid-cols-4 gap-6">
              {STATS.map((s, i) => (
                <Reveal key={s.label} delay={0.1 + i * 0.08} className="text-center">
                  <div className="mx-auto grid place-items-center h-16 w-16 rounded-full bg-brand-50 ring-1 ring-brand-100 text-brand-600 mb-3">
                    <s.icon className="h-7 w-7" strokeWidth={2} />
                  </div>
                  <p className="font-display font-extrabold text-navy-900 text-base leading-tight">{s.big}</p>
                  <p className="text-xs text-slate-500 mt-1">{s.label}</p>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================= NEDEN BİST ================= */}
      <section id="neden" className="relative overflow-hidden py-16 sm:py-24">
        <div className="absolute inset-0">
          <img src={ASSETS.nedenBg} alt="" className="h-full w-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-white via-white/92 to-white/30" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <div className="max-w-2xl">
            <Reveal>
              <div className="flex items-center gap-3">
                <span className="h-7 w-1 rounded bg-brand-600" />
                <h2 className="font-display font-black text-navy-900 text-2xl sm:text-3xl tracking-tight">NEDEN BİST?</h2>
              </div>
              <p className="mt-3 font-display font-semibold text-navy-800 text-lg">Türkiye Borsası'na yatırım yapmanın avantajları</p>
            </Reveal>
            <div className="mt-9 space-y-6">
              {ADVANTAGES.map((a, i) => (
                <Reveal key={a.h} delay={i * 0.1}>
                  <div className="flex gap-4 group">
                    <span className="shrink-0 grid place-items-center h-11 w-11 rounded-full bg-navy-800 text-brand-300 group-hover:bg-brand-600 group-hover:text-white transition-all">
                      <a.icon className="h-5 w-5" strokeWidth={2} />
                    </span>
                    <div>
                      <h3 className="font-display font-bold text-navy-900 text-base">{a.h}</h3>
                      <p className="mt-1 text-slate-600 text-sm leading-relaxed max-w-md">{a.p}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ================= GÖSTERGELER ================= */}
      <section className="bg-white py-16 sm:py-20">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="flex items-center gap-3">
              <span className="h-7 w-1 rounded bg-brand-600" />
              <h2 className="font-display font-black text-navy-900 text-xl sm:text-2xl tracking-tight">BİST'TEN ÖNEMLİ GÖSTERGELER</h2>
            </div>
          </Reveal>
          <div className="mt-9 grid lg:grid-cols-2 gap-8 items-center">
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3 gap-x-6 gap-y-8">
              {INDICATORS.map((ind, i) => (
                <Reveal key={ind.label} delay={i * 0.06}>
                  <div>
                    <p className="text-xs text-slate-500">{ind.label}</p>
                    <p className="font-display font-extrabold text-navy-900 text-xl mt-1 tabular-nums">{ind.value}</p>
                    <p className={`mt-1 inline-flex items-center gap-1 text-sm font-semibold ${ind.up ? "text-emerald-600" : "text-red-600"}`}>
                      {ind.up ? <ArrowUpRight className="h-4 w-4" /> : <ArrowDownRight className="h-4 w-4" />}
                      {ind.change}
                    </p>
                  </div>
                </Reveal>
              ))}
            </div>
            <Reveal delay={0.2}>
              <div className="rounded-xl bg-navy-900 p-5 shadow-xl">
                <p className="font-display font-semibold text-white/85 text-sm mb-3">BIST 100 – 5 Yıllık Grafik</p>
                <BistChart />
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ================= GELECEK ================= */}
      <section id="gelecek" className="relative overflow-hidden text-white py-20 sm:py-28">
        <div className="absolute inset-0">
          <img src={ASSETS.gelecekBg} alt="Türkiye'nin geleceği" className="h-full w-full object-cover" />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-navy-950 via-navy-950/85 to-navy-950/30" />
        <div className="relative z-10 max-w-7xl mx-auto px-5 sm:px-8">
          <Reveal>
            <div className="flex items-center gap-3">
              <span className="h-7 w-1 rounded bg-brand-400" />
              <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tight">GELECEK</h2>
            </div>
            <p className="mt-3 font-display font-semibold text-brand-300 text-lg sm:text-xl">Yükselen Türkiye, yükselen borsa</p>
          </Reveal>
          <div className="mt-7 space-y-5 max-w-2xl">
            <Reveal delay={0.15}>
              <p className="text-white/75 leading-relaxed text-sm sm:text-base">
                Türkiye ekonomisinin büyüme potansiyeli, güçlü demografik yapısı ve stratejik konumu, Borsa İstanbul'un gelecekte de yatırımcılar için cazip fırsatlar sunmaya devam edeceğini gösteriyor.
              </p>
            </Reveal>
            <Reveal delay={0.25}>
              <p className="text-white/75 leading-relaxed text-sm sm:text-base">
                Teknolojik yatırımlar, yeni ürün ve hizmetler, sürdürülebilirlik odaklı dönüşüm ile Borsa İstanbul, bölgesel bir finans merkezi olma yolunda emin adımlarla ilerliyor.
              </p>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ================= DISCLAIMER ================= */}
      <div className="bg-navy-950 border-t border-white/10">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 py-5 flex items-start gap-4">
          <span className="shrink-0 grid place-items-center h-9 w-9 rounded-full bg-white/10 text-brand-400">
            <Info className="h-5 w-5" />
          </span>
          <p className="text-xs sm:text-sm text-white/50 leading-relaxed">
            Yatırım, risk içerir. Bu web sitesinde yer alan bilgiler genel bilgilendirme amaçlıdır ve yatırım tavsiyesi niteliği taşımaz. Yatırım kararlarınızı almadan önce kendi araştırmanızı yapmanız veya bir uzmana danışmanız önemlidir.
          </p>
        </div>
      </div>

      <Footer />
    </div>
  );
}
